/**
 * The site's canonical origin.
 *
 * `www` and `https`, matching every canonical, `og:url` and sitemap entry the
 * site already publishes. It is a constant rather than something read from the
 * request: on a preview deployment the request origin is a `*.pages.dev`
 * hostname, and a canonical pointing at a preview build is how a preview ends
 * up competing with production in the index.
 */
export const SITE_URL = 'https://www.ainova.ch';

/**
 * The default sharing image. Absolute, because scrapers do not resolve
 * relative URLs.
 *
 * The platform screenshot rather than the logo: `logo.png` is 1824x272, a
 * ratio of 6.7, which every scraper either letterboxes or crops to a sliver.
 * `home.png` is 1679x979 — a ratio of 1.72 against the 1.91 that Open Graph
 * asks for, and large enough for a retina card. A purpose-made 1200x630
 * would still be better than either.
 */
export const OG_IMAGE = `${SITE_URL}/home.png`;

/** The company mark, for schema.org's `logo` — which does want the logo. */
export const LOGO = `${SITE_URL}/logo.png`;

/**
 * The publisher, as every schema block that needs one should state it.
 *
 * The root layout already emits a full `Organization` with address and UID.
 * This is the short reference form for use inside another entity — a
 * `BlogPosting`'s publisher, an offer's seller — so those do not each invent
 * their own spelling of the company name.
 */
export const PUBLISHER = {
  '@type': 'Organization',
  name: 'AINOVA',
  legalName: 'AMM StreamTech AG',
  url: SITE_URL,
  logo: LOGO
} as const;

type Faq = { question: string; answer: string };

/**
 * A `FAQPage` block built from the questions a page actually renders.
 *
 * Google requires the marked-up Q&A to be visible on the page, and
 * `firmenadressen-kaufen` was the one page with this schema: four questions,
 * hand-written, against the six the page rendered — one matching verbatim,
 * three paraphrased, two rendered questions missing. Paraphrased FAQ markup is
 * what gets a rich result dropped.
 *
 * Deriving it from the same array the component renders means the two cannot
 * disagree, which is the only durable fix.
 */
export function faqPage(faqs: Faq[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        // Answers may carry inline markup for emphasis; schema wants text.
        text: f.answer
          .replace(/<[^>]+>/g, '')
          .replace(/&amp;/g, '&')
          .trim()
      }
    }))
  };
}

/**
 * A `BreadcrumbList` from `[name, path]` pairs.
 *
 * Paths are site-relative; the root is supplied automatically because every
 * breadcrumb on this site starts there.
 */
export function breadcrumbs(trail: Array<[string, string]>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [['Home', ''] as [string, string], ...trail].map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`
    }))
  };
}

/** A `Service` entity for one of the two product lines. */
export function service(opts: {
  name: string;
  description: string;
  path: string;
  serviceType: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: opts.name,
    description: opts.description,
    serviceType: opts.serviceType,
    url: `${SITE_URL}${opts.path}`,
    provider: PUBLISHER,
    areaServed: { '@type': 'Country', name: 'Switzerland' },
    audience: { '@type': 'BusinessAudience', name: 'B2B' }
  };
}

/**
 * A `BlogPosting` from a post's front matter.
 *
 * The eleven articles carry `title`, `excerpt`, `author`, `date`, `image` and
 * `category` already and published none of it as structured data, so nothing
 * told a crawler who wrote them or when. For the AI-recommendation channel
 * that is the difference between a quotable source and an anonymous page.
 */
export function blogPosting(post: {
  slug: string;
  title: string;
  excerpt?: string;
  author?: string;
  date?: string;
  image?: string;
  category?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image ? `${SITE_URL}${post.image}` : OG_IMAGE,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Organization', name: post.author || 'AINOVA' },
    publisher: PUBLISHER,
    articleSection: post.category,
    inLanguage: 'de-CH',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/de/blog/${post.slug}`
    }
  };
}
